import {
  FIELD_NAMES,
  PRICER_NODES_COLORS,
  RowColor,
} from "constants/pricerTable";
import {
  extractCopyShortHandForSummaryRows,
  getDataFromLocalStorage,
  setDataToLocalStorage,
} from "../generalHelper";
import { COLORS } from "constants/agGridCommonStyle";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import { PRICER_ROW_ACTION_MODAL } from "constants/pricerRowActions";
import { deleteRow, duplicateRow, updateOptionRowsActiveStatus } from "api/pricerApi";
import { triggerPushOptionToTeamRunAPI } from "api/teamRunApi";
import { handleCopyShortHand, toast } from "../generalHelper";
import { PAYOUT_PRICING_SIDE_DEFAULT } from "constants/payout";
import { fetchAndCopyShorthandWithAnalysis } from "../payoutAnalysisShareHelper";
import {
  isPayoutCompareSelectionCount,
  uniqueSummaryIdsFromRows,
} from "../payoutCompareApi";
import { ACTIONS, CATEGORIES, COMPONENT_NAMES, CONTEXTS, DESCRIPTIONS, ELEMENTS, EVENT_NAMES, sendEvent } from "../googleAnalyticsHelper";

// Which popup the backend asks for after a push to team run.
const PUSH_TO_TEAM_RUN_POPUP_TYPE = {
  UPDATE_OPTION: 1,
  PUSH_OPTION: 2,
  PUSH_OPTION_WITH_CONFIRMATION: 3,
};

const getRowDataFromProps = (props) =>
  props.node.group ? props.node.aggData : props.node.data;

export const handleColorMenu = (key, selectedRows, latestPropsRef) => {
  let nodesColorsArr = [];
  const nodesColorsArrSavedValue = getDataFromLocalStorage(PRICER_NODES_COLORS);
  if (nodesColorsArrSavedValue) {
    nodesColorsArr = nodesColorsArrSavedValue;
  }
  selectedRows.forEach((item) => {
    nodesColorsArr.push({ row_color: key, summary_id: item.summary_id });
  });
  const ids = nodesColorsArr?.map((i) => i.summary_id);
  const filterdColorsArray = nodesColorsArr?.filter(
    ({ summary_id }, index) => !ids.includes(summary_id, index + 1)
  );
  setDataToLocalStorage(PRICER_NODES_COLORS, filterdColorsArray);
  sendEvent({
    category: CATEGORIES.PRICER,
    context: CONTEXTS.PRICER_ACTION_MENU,
    action: ACTIONS.CLICK,
    eventName: EVENT_NAMES.PRICER_CHANGE_ROW_COLOR_MENU,
    extraParams: { color: key },
  });
  latestPropsRef.current.api.forEachNode(function (node) {
    const nodeData = node.group ? node.aggData : node.data;
    selectedRows.forEach((item) => {
      if (nodeData.summary_id === item.summary_id) {
        node.setDataValue(RowColor, key);
        latestPropsRef.current.api?.refreshCells({
          rowNodes: [node],
          columns: ["tenor"],
          force: true,
        });
      }
    });
  });
};

/**
 * Pushes the option to team run and asks the row-action modal host to show
 * whichever popup the backend answered with.
 */
export const pushOptionToTeamRun = async ({
  summaryId,
  productId,
  fromMenu = false,
  openRowActionModal,
  setIsPushingToTeamRunOrAuditTrail,
}) => {
  setIsPushingToTeamRunOrAuditTrail(true);
  sendEvent({
    category: CATEGORIES.TEAM_RUN,
    description: DESCRIPTIONS.PUSH_TO_TEAM_RUN,
    context: fromMenu ? CONTEXTS.PRICER_ACTION_MENU : CONTEXTS.PRICER,
    action: ACTIONS.CLICK,
    element: ELEMENTS.PUSH_TO_TEAM_RUN,
    component: fromMenu
      ? COMPONENT_NAMES.MENU_BUTTON
      : COMPONENT_NAMES.ICON_BUTTON,
    eventName: fromMenu
      ? EVENT_NAMES.PRICER_PUSH_TO_TEAM_RUN_MENU
      : EVENT_NAMES.PRICER_PUSH_TO_TEAM_RUN_ICON,
  });

  try {
    const responseData = (
      await triggerPushOptionToTeamRunAPI({ summary_id: summaryId })
    ).data.data;
    const popupType = responseData["popup_type"];
    const quantity = {
      ask_quantity: responseData.ask_quantity,
      bid_quantity: responseData.bid_quantity,
    };

    if (popupType === PUSH_TO_TEAM_RUN_POPUP_TYPE.UPDATE_OPTION) {
      openRowActionModal(PRICER_ROW_ACTION_MODAL.UPDATE_OPTION, {
        data: responseData.rows,
        colors: responseData,
        tradingCompanies: responseData.trading_companies,
        quantity,
        summaryId,
        productId,
        agreedDeltas: responseData.agreed_deltas ?? [],
      });
    }

    if (
      popupType === PUSH_TO_TEAM_RUN_POPUP_TYPE.PUSH_OPTION ||
      popupType === PUSH_TO_TEAM_RUN_POPUP_TYPE.PUSH_OPTION_WITH_CONFIRMATION
    ) {
      openRowActionModal(PRICER_ROW_ACTION_MODAL.PUSH_OPTION, {
        data: responseData,
        tradingCompanies: responseData.trading_companies,
        quantity,
        summaryId,
        productId,
      });
    }
  } catch (err) {
    console.log({ err });
  } finally {
    setIsPushingToTeamRunOrAuditTrail(false);
  }
};

export const handleMenuItemClick = (
  itemClicked,
  selectedRows,
  latestPropsRef,
  {
    refetchOptionRow,
    setIsCellEditing,
    openRowActionModal,
    openPayoutModal,
    openPayoutCompareModal,
    onPushToRunClick,
    onPushToAuditTrailClick,
    getShowFormulasOpenPayload,
  }
) => {
  if (itemClicked === COLORS[3].name) {
    handleColorMenu(itemClicked, selectedRows, latestPropsRef);
    return;
  }
  if (
    itemClicked === COLORS[0].name ||
    itemClicked === COLORS[1].name ||
    itemClicked === COLORS[2].name
  ) {
    handleColorMenu(itemClicked, selectedRows, latestPropsRef);
    return;
  }
  const extractSummaryIds = selectedRows.map(
    (item) => item[FIELD_NAMES.SUMMARY_ID]
  );
  if (itemClicked === ACTION_BUTTONS_NAME.COPY_SHORTHAND.key) {
    extractCopyShortHandForSummaryRows(latestPropsRef.current);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_MENU,
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.COPY_SHORTHAND_WITH_ANALYSIS.key) {
    const rowData = latestPropsRef.current.node.group
      ? latestPropsRef.current.node.aggData
      : latestPropsRef.current.node.data;
    const summaryId =
      rowData?.[FIELD_NAMES.SUMMARY_ID] ??
      rowData?.[FIELD_NAMES.SUMMARY_REFERENCE_ID];
    const shorthand = rowData?.[FIELD_NAMES.COPY_SHORTHAND];

    if (!summaryId || !shorthand) {
      toast("Unable to copy shorthand with analysis.", "warning");
      return;
    }

    setIsCellEditing(true);
    fetchAndCopyShorthandWithAnalysis(
      summaryId,
      shorthand,
      PAYOUT_PRICING_SIDE_DEFAULT
    )
      .then(() => {
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER_ACTION_MENU,
          action: ACTIONS.CLICK,
          eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_WITH_ANALYSIS_MENU,
        });
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Unable to create payout analysis link.";
          console.log(message);
      })
      .finally(() => setIsCellEditing(false));
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.COPY_SHORTHAND_NO_VOLS.key) {
    extractCopyShortHandForSummaryRows(latestPropsRef.current, false);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_NO_VOLS_MENU,
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.COPY_SHORTHAND_NO_VOL_PREM.key) {
    extractCopyShortHandForSummaryRows(
      latestPropsRef.current,
      false,
      false,
      false,
      true
    );
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_NO_VOL_PREM_MENU,
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.COPY_SHORTHAND_LEGS.key) {
    const rowData = latestPropsRef.current.node.group
      ? latestPropsRef.current.node.aggData
      : latestPropsRef.current.node.data;
    handleCopyShortHand(rowData.copy_shorthand_legs);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_COPY_SHORTHAND_LEGS_MENU,
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.DUPLICATE.key) {
    duplicateRow(extractSummaryIds).then((res) => {
      refetchOptionRow();
      toast(res?.data?.message, "success");
       sendEvent({
         category: CATEGORIES.PRICER,
         description: DESCRIPTIONS.DUPLICATE,
         context: CONTEXTS.PRICER_ACTION_MENU,
         action: ACTIONS.CLICK,
         element: ELEMENTS.DUPLICATE,
         component: COMPONENT_NAMES.MENU_BUTTON,
         eventName: EVENT_NAMES.PRICER_DUPLICATE_MENU,
       });
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.DELETE.key) {
    setIsCellEditing(true);
    deleteRow(extractSummaryIds).then((res) => {
      
      setIsCellEditing(false);
      refetchOptionRow();
      toast(res?.data?.message, "success");
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER_ACTION_MENU,
        action: ACTIONS.CLICK,
        eventName: EVENT_NAMES.PRICER_DELETE_MENU,
      });
    })
      .catch((err) => {
        setIsCellEditing(false);
      });
    return;
  }

  if (
    itemClicked === ACTION_BUTTONS_NAME.MAKE_ACTIVE.key ||
    itemClicked === ACTION_BUTTONS_NAME.MAKE_IN_ACTIVE.key
  ) {
    const isActive =
      itemClicked === ACTION_BUTTONS_NAME.MAKE_ACTIVE.key ? 1 : 0;
    const summaryIds = [
      ...new Set(extractSummaryIds.filter((id) => id != null)),
    ];
    if (!summaryIds.length) return;

    setIsCellEditing(true);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName:
        isActive === 1
          ? EVENT_NAMES.PRICER_MAKE_ACTIVE_MENU
          : EVENT_NAMES.PRICER_MAKE_INACTIVE_MENU,
    });
    updateOptionRowsActiveStatus(summaryIds, isActive)
      .then((res) => {
        refetchOptionRow();
        toast(res?.data?.message, "success");
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Unable to update active status.";
        toast(message, "error");
      })
      .finally(() => setIsCellEditing(false));
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.OTHER_INFO_AND_DETAILS.key) {
    openRowActionModal(PRICER_ROW_ACTION_MODAL.OTHER_INFO, {
      rowData: getRowDataFromProps(latestPropsRef.current),
    });
    refetchOptionRow();
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_OTHER_INFO_MENU,
    });
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.TRADE.key) {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_TRADE_MENU,
    });
    // EditTradeDetails resolves the summary id from this ag-grid shaped snapshot,
    // so the modal no longer holds on to the live cell renderer props.
    openRowActionModal(PRICER_ROW_ACTION_MODAL.TRADE, {
      rowSelectedInfo: {
        data: latestPropsRef.current.data,
        node: { aggData: latestPropsRef.current.node?.aggData },
      },
    });
    refetchOptionRow();
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.PUSH_TO_TEAM_RUN.key) {
    onPushToRunClick(true);
  }

  if (itemClicked === ACTION_BUTTONS_NAME.PUSH_TO_AUDIT_TRAIL.key) {
    onPushToAuditTrailClick();
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_PUSH_TO_AUDIT_TRAIL_MENU,
    });
  }

  if (itemClicked === ACTION_BUTTONS_NAME.PAYOUT.key) {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_PAYOUT_MENU,
    });
    const rowData = getRowDataFromProps(latestPropsRef.current);
    const summaryId =
      rowData?.[FIELD_NAMES.SUMMARY_ID] ??
      rowData?.[FIELD_NAMES.SUMMARY_REFERENCE_ID];
    if (summaryId != null) {
      openPayoutModal(summaryId);
    }
    return;
  }

  if (itemClicked === ACTION_BUTTONS_NAME.PAYOUT_COMPARE.key) {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_PAYOUT_COMPARE_MENU,
    });
    const summaryIds = uniqueSummaryIdsFromRows(selectedRows);
    if (isPayoutCompareSelectionCount(summaryIds.length) && openPayoutCompareModal) {
      openPayoutCompareModal(summaryIds);
    }
    return;
  }

   if (itemClicked === ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key) {
     sendEvent({
       category: CATEGORIES.PRICER,
       context: CONTEXTS.PRICER_ACTION_MENU,
       action: ACTIONS.CLICK,
       eventName: EVENT_NAMES.PRICER_ADDITIONAL_DATA_MENU,
     });
     const rowData = getRowDataFromProps(latestPropsRef.current);
     openRowActionModal(PRICER_ROW_ACTION_MODAL.ADDITIONAL_DATA, {
       rowData: {
         ...rowData,
         [FIELD_NAMES.SUMMARY_ID]:
           rowData?.[FIELD_NAMES.SUMMARY_ID] ??
           rowData?.[FIELD_NAMES.SUMMARY_REFERENCE_ID],
       },
     });
     return;
   }

  if (itemClicked === ACTION_BUTTONS_NAME.SHOW_FORMULAS.key) {
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER_ACTION_MENU,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_SHOW_FORMULAS_MENU,
    });
    const payload = getShowFormulasOpenPayload?.();
    if (payload?.summaryId != null) {
      openRowActionModal(PRICER_ROW_ACTION_MODAL.SHOW_FORMULAS, payload);
    }
    return;
  }
};
